import { Button, Stack, Typography } from '@mui/material';
import { useColorScheme } from 'hooks/useColorScheme';
import { InlineStatus } from 'components/InlineStatus';
import { StatItem, StatTable, getStatItemSize } from 'components/StatTable';
import { subHours } from 'date-fns';
import { useAQISensorWithObservations } from 'features/aqiSensors/hooks/useAQISensorWithObservations';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { useTabs } from 'hooks/useTabs';
import { MouseEvent, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getTimeAgo } from 'shared/dates';
import { WeatherAlertList } from 'features/weatherAlerts/components/WeatherAlertList';
import { useAQILevelsChart } from 'features/aqiSensors/hooks/useAQILevelsChart';
import { EchartsWrapper } from 'libs/echarts/EchartsWrapper';
import { ChartLoadingGuard } from 'components/ChartLoadingGuard';
import { useRequireMapLayerForRouterEntity } from 'hooks/useRequireMapLayerForRouterEntity';
import { MapLayers } from 'features/mapping/mapping.constants';
import { getCurrentLocationAsShareUrl } from 'shared/utils';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import {
  getLast24HoursObservations,
  getLatestObservation,
} from './AQIDetails.utils';
import { AQIStatValue } from './AQIStatValue';
import {
  EMPTY_DATA_VALUE,
  WEATHER_OBSERVATIONS_POLL_INTERVAL,
} from '../../constants';

const TAB_PANEL_ID_PREFIX = 'aqi-sensor-tab-panel';
const ATTRIBUTION_URL = 'https://www2.purpleair.com/?ref=watchduty';

export const AQIDetails = (): JSX.Element => {
  const { sensorId } = useParams<{ sensorId: string }>();
  const { t } = useTranslation();
  const observationsHistoryStart = subHours(new Date(), 24);
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const { isDarkMode } = useColorScheme();

  useRequireMapLayerForRouterEntity(MapLayers.PURPLE_AIR);

  const {
    aqiSensor,
    observations,
    isLoadingStation,
    isLoadingObservations,
    aqiSensorError,
    observationsError,
  } = useAQISensorWithObservations({
    id: parseInt(sensorId, 10),
    start: observationsHistoryStart,
    end: new Date(),
    pollInterval: WEATHER_OBSERVATIONS_POLL_INTERVAL,
  });

  const {
    minTimestamp,
    maxTimestamp,
    observations: last24HoursObservations,
  } = useMemo(() => {
    // Uncomment to experiment with values and review chart behavior
    // const testObservations = [
    //   aqiSensorObservationFactory.build({
    //     aqi: 45,
    //     timestamp: subHours(new Date(), 24),
    //   }),
    //   aqiSensorObservationFactory.build({
    //     aqi: 50,
    //     timestamp: subHours(new Date(), 18),
    //   }),
    //   aqiSensorObservationFactory.build({
    //     aqi: 150,
    //     timestamp: subHours(new Date(), 12),
    //   }),
    //   aqiSensorObservationFactory.build({
    //     aqi: 155,
    //     timestamp: subHours(new Date(), 6),
    //   }),
    //   aqiSensorObservationFactory.build({
    //     aqi: 183,
    //     timestamp: new Date(),
    //   }),
    // ];
    // return getLast24HoursObservations(testObservations);
    return getLast24HoursObservations(observations);
  }, [observations]);

  const aqiLevelsChartOptions = useAQILevelsChart({
    observations: last24HoursObservations,
    aqiSensor,
    minTimestamp,
    maxTimestamp,
  });

  const handleClickAttribution = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();

    await Browser.open({
      url: ATTRIBUTION_URL,
      presentationStyle: 'popover',
    });
  };

  const isOffline = Boolean(aqiSensor?.isOffline);
  const hasObservations = Boolean(observations?.length);
  const canDisplayChart = !isOffline || hasObservations;
  const isChartDataLoading = isLoadingObservations;

  const tabData = [
    {
      tabId: 'conditions',
      label: t('aqiSensors.tabs.conditions'),
      enabled: true,
      content: canDisplayChart && aqiLevelsChartOptions && (
        <ChartLoadingGuard
          isLoading={isChartDataLoading}
          error={Boolean(observationsError)}
          skeletonHeight={360}
          sx={{ m: 2 }}
        >
          <Stack spacing={3}>
            <EchartsWrapper
              option={aqiLevelsChartOptions}
              style={{ minHeight: 360 }}
            />

            <Stack spacing={1.5} alignItems="center">
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 'medium',
                }}
              >
                {t('aqiSensors.providedBy')}
              </Typography>

              <a
                href={ATTRIBUTION_URL}
                target="_blank"
                rel="noreferrer"
                style={{ textAlign: 'center', maxWidth: 205 }}
                onClick={handleClickAttribution}
              >
                <img
                  src="/images/purpleair.png"
                  alt="PurpleAir"
                  style={{
                    maxWidth: '100%',
                    filter: isDarkMode ? 'brightness(0) invert(1)' : undefined,
                  }}
                />
              </a>
            </Stack>
          </Stack>
        </ChartLoadingGuard>
      ),
    },
    {
      tabId: 'notifications',
      label: t('aqiSensors.tabs.notifications'),
      enabled: Capacitor.isNativePlatform(),
      content: (
        <WeatherAlertList
          station={
            aqiSensor
              ? { id: aqiSensor.id, weatherStationType: 'aqi' }
              : undefined
          }
          fromRoute={`/aqi/${aqiSensor?.id}?tab=notifications`}
          showStationName={false}
        />
      ),
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('aqiSensors.tabs.ariaLabel'),
    tabData,
  });

  if (isLoadingStation || aqiSensorError || !aqiSensor) {
    return <InlineStatus status={isLoadingStation ? 'loading' : 'error'} />;
  }

  const latestObservation = getLatestObservation(observations);

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: t('home.aqiSensorTitle', { name: aqiSensor.name }),
      url: getCurrentLocationAsShareUrl(latestObservation?.timestamp),
    });
  };

  const stats: StatItem[] = [
    {
      label: t('aqiSensors.aqi'),
      value:
        typeof latestObservation?.aqi === 'number' ? (
          <AQIStatValue aqi={latestObservation.aqi} />
        ) : (
          EMPTY_DATA_VALUE
        ),
    },
    {
      label: t('aqiSensors.status'),
      value: aqiSensor.isOffline
        ? t('aqiSensors.offline')
        : t('aqiSensors.online'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('home.aqiSensorTitle', { name: aqiSensor.name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header title={aqiSensor.name} />

        <MapEntityDetailsLayout.Body>
          <StatTable stats={stats} itemSize={getStatItemSize(stats.length)} />

          {latestObservation && (
            <MapEntityDetailsLayout.Meta>
              <Typography variant="body2">
                <Trans
                  i18nKey="aqiSensors.updated"
                  values={{
                    timeAgo: getTimeAgo(
                      latestObservation.timestamp.toISOString(),
                    ),
                  }}
                >
                  Last updated: <b>5 minutes ago</b>
                </Trans>
              </Typography>
            </MapEntityDetailsLayout.Meta>
          )}

          {canShareOrCopy && (
            <MapEntityDetailsLayout.Actions>
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                onClick={handleShare}
                size="large"
              >
                {t('common.share')}
              </Button>
            </MapEntityDetailsLayout.Actions>
          )}
        </MapEntityDetailsLayout.Body>

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
